import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import AdminNav from '../components/AdminNav';
import { 
  ScanLine, Loader2, Search, Eye, 
  User, Building, Mail, Phone, Calendar, 
  RefreshCw, Trash2, RotateCcw, CheckCircle,
  XCircle, Clock, Filter, Pencil, Save
} from 'lucide-react';

const ADMIN_EMAILS = ['sagarshiva111555@gmail.com', 'allgrowtechonologies@gmail.com'];

const AdminScannedContacts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, processing: 0, completed: 0, failed: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userCard, setUserCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(true);
  
  // Dialog states
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    phone2: '',
    location: '',
    locationAddress: '',
    websiteName: '',
    websiteTLD: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    facebook: '',
    notes: ''
  });

  // Check admin access
  useEffect(() => {
    if (user && !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
      toast({
        title: "Access Denied",
        description: "Only admin can access this page",
        variant: "destructive"
      });
      navigate('/');
    }
  }, [user, navigate, toast]);

  // Load user's card for layout
  useEffect(() => {
    const loadUserCard = async () => {
      if (!user) {
        setCardLoading(false);
        return;
      }
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cards/my-cards`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) setUserCard(data[0]);
        }
      } catch (error) {
        console.error('Error loading user card:', error);
      } finally {
        setCardLoading(false);
      }
    };
    loadUserCard();
  }, [user]);

  // Load contacts
  const loadContacts = useCallback(async () => {
    if (!user?.email || !ADMIN_EMAILS.includes(user.email.toLowerCase())) return;
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        status: statusFilter
      });
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scanned-contacts/admin/all?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
        setStats(data.stats);
        setTotal(data.total);
        setTotalPages(data.totalPages);
      } else {
        throw new Error('Failed to load contacts');
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast({
        title: "Error",
        description: "Failed to load scanned contacts",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [user, page, statusFilter, searchQuery, toast]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadContacts();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleDelete = async () => {
    if (!selectedContact) return;
    
    try {
      setActionLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scanned-contacts/admin/${selectedContact._id}`,
        { 
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        toast({ title: "Success", description: "Contact deleted successfully" });
        setShowDeleteDialog(false);
        setSelectedContact(null);
        loadContacts();
      } else {
        throw new Error('Failed to delete contact');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReprocess = async (contact) => {
    try {
      setActionLoading(true);
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scanned-contacts/admin/${contact._id}/reprocess`,
        { 
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` } 
        }
      );
      
      if (response.ok) {
        toast({ title: "Success", description: "Reprocessing started" });
        loadContacts();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to reprocess');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to start reprocessing",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = (contact) => {
    setSelectedContact(contact);
    setEditForm({
      name: contact.name === 'Processing Failed' || contact.name === 'Processing Timeout' ? '' : (contact.name || ''),
      title: contact.title || '',
      company: contact.company === 'Processing took too long. Please try again.' || contact.company?.startsWith('Error:') ? '' : (contact.company || ''),
      email: contact.email || '',
      phone: contact.phone || '',
      phone2: contact.phone2 || '',
      location: contact.location || '',
      locationAddress: contact.locationAddress || '',
      websiteName: contact.websiteName || '',
      websiteTLD: contact.websiteTLD || '',
      linkedin: contact.linkedin || '',
      twitter: contact.twitter || '',
      instagram: contact.instagram || '',
      facebook: contact.facebook || '',
      notes: contact.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedContact) return;
    
    try {
      setActionLoading(true);
      const token = await user.getIdToken();
      
      // Prepare update data - mark as completed
      const updateData = {
        ...editForm,
        isProcessing: false,
        manuallyCompleted: true,
        manuallyCompletedAt: new Date().toISOString(),
        manuallyCompletedBy: user.email
      };
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/scanned-contacts/${selectedContact._id}`,
        { 
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );
      
      if (response.ok) {
        toast({ title: "Success", description: "Contact updated successfully" });
        setShowEditDialog(false);
        setSelectedContact(null);
        loadContacts();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update contact');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update contact",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (contact) => {
    if (contact.isProcessing) {
      return <Badge className="bg-yellow-500"><Clock className="w-3 h-3 mr-1" />Processing</Badge>;
    }
    if (contact.name === 'Processing Failed' || contact.name === 'Processing Timeout' || contact.error) {
      return <Badge className="bg-red-600"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
    }
    return <Badge className="bg-green-600"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
  };

  if (!user || !ADMIN_EMAILS.includes(user.email?.toLowerCase())) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <ScanLine className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
            <p className="text-gray-500 mt-2">Only admin can access this page</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ScanLine className="w-8 h-8 text-teal-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Scanned Contacts</h1>
                <p className="text-gray-600">Manage all scanned visiting cards</p>
              </div>
            </div>
            <Button
              onClick={loadContacts}
              variant="outline"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className={`border-2 cursor-pointer transition-all ${statusFilter === 'all' ? 'border-teal-500 bg-teal-50' : 'border-teal-100 hover:border-teal-300'}`}
            onClick={() => { setStatusFilter('all'); setPage(1); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Scanned</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <ScanLine className="w-8 h-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-2 cursor-pointer transition-all ${statusFilter === 'completed' ? 'border-green-500 bg-green-50' : 'border-green-100 hover:border-green-300'}`}
            onClick={() => { setStatusFilter('completed'); setPage(1); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-2 cursor-pointer transition-all ${statusFilter === 'processing' ? 'border-yellow-500 bg-yellow-50' : 'border-yellow-100 hover:border-yellow-300'}`}
            onClick={() => { setStatusFilter('processing'); setPage(1); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Processing</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.processing}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-2 cursor-pointer transition-all ${statusFilter === 'failed' ? 'border-red-500 bg-red-50' : 'border-red-100 hover:border-red-300'}`}
            onClick={() => { setStatusFilter('failed'); setPage(1); }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="border-2 border-gray-100 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, company, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Showing {total} {statusFilter !== 'all' ? statusFilter : ''} contacts
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contacts List */}
        <Card className="border-2 border-gray-100">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-teal-600" />
              Scanned Contacts ({total})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ScanLine className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">
                  {searchQuery ? 'No contacts match your search' : 'No scanned contacts yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <Card 
                    key={contact._id} 
                    className={`border-2 transition-colors ${
                      contact.isProcessing 
                        ? 'border-yellow-200 bg-yellow-50/50' 
                        : contact.name === 'Processing Failed' || contact.error
                        ? 'border-red-200 bg-red-50/50'
                        : 'border-gray-100 hover:border-teal-200'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          {/* Card Image */}
                          <div className="w-20 h-14 rounded bg-gray-200 flex-shrink-0 overflow-hidden">
                            {contact.frontImage ? (
                              <img 
                                src={contact.frontImage} 
                                alt="Card"
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => { setSelectedContact(contact); setShowDetailDialog(true); }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <ScanLine className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          {/* Contact Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-gray-900 truncate">
                                {contact.name || 'Unknown'}
                              </h3>
                              {getStatusBadge(contact)}
                              {contact.reviewed && (
                                <Badge variant="outline" className="text-xs border-green-500 text-green-600">
                                  Reviewed
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-gray-600">
                              {contact.company && (
                                <div className="flex items-center gap-1">
                                  <Building className="w-3 h-3" />
                                  <span className="truncate">{contact.company}</span>
                                </div>
                              )}
                              {contact.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  <span className="truncate">{contact.email}</span>
                                </div>
                              )}
                              {contact.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  <span>{contact.phone}</span>
                                </div>
                              )}
                              {contact.title && (
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span className="truncate">{contact.title}</span>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{formatDate(contact.createdAt)}</span>
                              </div>
                              {contact.reprocessedAt && (
                                <div className="flex items-center gap-1 text-blue-600">
                                  <RotateCcw className="w-3 h-3" />
                                  <span>Reprocessed: {formatDate(contact.reprocessedAt)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedContact(contact); setShowDetailDialog(true); }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {(contact.isProcessing || contact.name === 'Processing Failed' || contact.name === 'Processing Timeout' || contact.error) && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                onClick={() => handleReprocess(contact)}
                                disabled={actionLoading}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reprocess
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                className="bg-amber-600 hover:bg-amber-700"
                                onClick={() => openEditDialog(contact)}
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Complete Manually
                              </Button>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => { setSelectedContact(contact); setShowDeleteDialog(true); }}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-teal-600" />
              Contact Details
            </DialogTitle>
          </DialogHeader>
          {selectedContact && (
            <div className="space-y-4">
              {/* Images */}
              <div className="grid grid-cols-2 gap-4">
                {selectedContact.frontImage && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Front Image</p>
                    <img 
                      src={selectedContact.frontImage} 
                      alt="Front" 
                      className="w-full rounded-lg border"
                    />
                  </div>
                )}
                {selectedContact.backImage && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Back Image</p>
                    <img 
                      src={selectedContact.backImage} 
                      alt="Back" 
                      className="w-full rounded-lg border"
                    />
                  </div>
                )}
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Name</p>
                  <p className="text-gray-900">{selectedContact.name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Title</p>
                  <p className="text-gray-900">{selectedContact.title || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Company</p>
                  <p className="text-gray-900">{selectedContact.company || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Email</p>
                  <p className="text-gray-900">{selectedContact.email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Phone</p>
                  <p className="text-gray-900">{selectedContact.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Phone 2</p>
                  <p className="text-gray-900">{selectedContact.phone2 || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Location</p>
                  <p className="text-gray-900">{selectedContact.location || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Website</p>
                  <p className="text-gray-900">
                    {selectedContact.websiteName ? `${selectedContact.websiteName}${selectedContact.websiteTLD}` : '-'}
                  </p>
                </div>
              </div>
              
              {/* Status Info */}
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-600">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedContact)}</div>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">Created</p>
                    <p className="text-gray-900">{formatDate(selectedContact.createdAt)}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">User ID</p>
                    <p className="text-gray-900 text-xs font-mono">{selectedContact.userId}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">Contact ID</p>
                    <p className="text-gray-900 text-xs font-mono">{selectedContact._id}</p>
                  </div>
                </div>
              </div>
              
              {/* Error Info */}
              {selectedContact.error && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-red-600 mb-2">Error Details</p>
                  <pre className="text-xs bg-red-50 p-3 rounded-lg overflow-x-auto text-red-800">
                    {selectedContact.error}
                  </pre>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Contact?
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Are you sure you want to delete this scanned contact? This action cannot be undone.
          </p>
          {selectedContact && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="font-medium">{selectedContact.name}</p>
              <p className="text-sm text-gray-500">{selectedContact.company}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Complete Manually Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Pencil className="w-5 h-5" />
              Complete Contact Manually
            </DialogTitle>
          </DialogHeader>
          
          {selectedContact && (
            <div className="space-y-4">
              {/* Card Image Preview */}
              {selectedContact.frontImage && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-600 mb-2">Card Image (Reference)</p>
                  <img 
                    src={selectedContact.frontImage} 
                    alt="Card" 
                    className="w-full max-w-md rounded-lg border"
                  />
                </div>
              )}
              
              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name *</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    placeholder="Contact name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    placeholder="Job title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-company">Company</Label>
                  <Input
                    id="edit-company"
                    value={editForm.company}
                    onChange={(e) => setEditForm({...editForm, company: e.target.value})}
                    placeholder="Company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    placeholder="Email address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                    placeholder="Phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-phone2">Phone 2</Label>
                  <Input
                    id="edit-phone2"
                    value={editForm.phone2}
                    onChange={(e) => setEditForm({...editForm, phone2: e.target.value})}
                    placeholder="Alternate phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-location">Location</Label>
                  <Input
                    id="edit-location"
                    value={editForm.location}
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    placeholder="City/Location"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-address">Address</Label>
                  <Input
                    id="edit-address"
                    value={editForm.locationAddress}
                    onChange={(e) => setEditForm({...editForm, locationAddress: e.target.value})}
                    placeholder="Full address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-website">Website Name</Label>
                  <Input
                    id="edit-website"
                    value={editForm.websiteName}
                    onChange={(e) => setEditForm({...editForm, websiteName: e.target.value})}
                    placeholder="example"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tld">Website TLD</Label>
                  <Input
                    id="edit-tld"
                    value={editForm.websiteTLD}
                    onChange={(e) => setEditForm({...editForm, websiteTLD: e.target.value})}
                    placeholder=".com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-linkedin">LinkedIn</Label>
                  <Input
                    id="edit-linkedin"
                    value={editForm.linkedin}
                    onChange={(e) => setEditForm({...editForm, linkedin: e.target.value})}
                    placeholder="LinkedIn URL"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-twitter">Twitter</Label>
                  <Input
                    id="edit-twitter"
                    value={editForm.twitter}
                    onChange={(e) => setEditForm({...editForm, twitter: e.target.value})}
                    placeholder="Twitter handle"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  placeholder="Additional notes"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSubmit}
              disabled={actionLoading || !editForm.name.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminScannedContacts;
